import React, { useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Eye } from 'lucide-react';
import { TOKENS, COLOR_POR_ESTADO } from '../state/colors';
import { fs } from '../state/typography';
import { formatMoneda, diasHasta } from '../state/format';
import { EstadoBolsa, ESTADOS_LABEL, ORDEN_KANBAN, esTransicionValida, esTransicionSoloSistema } from '../state/bolsaMachine';
import { BolsaCentro } from '../types';
import { useToast } from './Toast';

interface KanbanBoardProps {
  cotizaciones: BolsaCentro[];
  onAbrirDrawer: (id: string) => void;
  onSoltarEnviada: (bolsaId: string) => void;
  onTransicionar: (bolsaId: string, to: EstadoBolsa) => void;
  onVerDetalle: (id: string) => void;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({ cotizaciones, onAbrirDrawer, onSoltarEnviada, onTransicionar, onVerDetalle }) => {
  const { showToast } = useToast();
  const [arrastrando, setArrastrando] = useState<{ id: string; estado: EstadoBolsa } | null>(null);
  const [columnaHover, setColumnaHover] = useState<EstadoBolsa | null>(null);

  const handleDrop = (columna: EstadoBolsa) => {
    setColumnaHover(null);
    if (!arrastrando) return;
    const { id, estado: origen } = arrastrando;
    setArrastrando(null);

    if (columna === origen) return;

    if (esTransicionSoloSistema(origen, columna)) {
      showToast('"Vencida" es una transición automática del sistema', 'error');
      return;
    }
    if (!esTransicionValida(origen, columna)) {
      showToast(`Transición no válida: ${ESTADOS_LABEL[origen]} → ${ESTADOS_LABEL[columna]}`, 'error');
      return;
    }
    if (columna === EstadoBolsa.ENVIADA) {
      onSoltarEnviada(id);
      return;
    }
    onTransicionar(id, columna);
  };

  return (
    <Box>
      <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), color: TOKENS.muted, marginBottom: '14px' }}>
        Arrastra para cambiar de estado — solo se habilitan las transiciones válidas. Vencer es automático por
        fecha de validez; Ganada, Perdida y Anulada son estados terminales.
      </Typography>

      <Box sx={{ display: 'flex', gap: '12px', overflowX: 'auto', alignItems: 'flex-start' }}>
        {ORDEN_KANBAN.map((columna) => {
          const items = cotizaciones.filter((b) => b.estado_codigo === columna);
          const total = items.reduce((acc, b) => acc + b.total, 0);
          const esValida = arrastrando ? esTransicionValida(arrastrando.estado, columna) && arrastrando.estado !== columna : null;
          const hover = columnaHover === columna;

          return (
            <Box
              key={columna}
              data-testid={`kanban-column-${columna}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (columnaHover !== columna) setColumnaHover(columna);
              }}
              onDragLeave={() => setColumnaHover((c) => (c === columna ? null : c))}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(columna);
              }}
              sx={{
                flex: '1 1 0',
                minWidth: 220,
                backgroundColor: hover && esValida ? '#F6EEF6' : TOKENS.wash,
                border: hover && esValida ? `1.5px dashed ${TOKENS.purpleSoft}` : `1px solid ${TOKENS.hair}`,
                borderRadius: '18px',
                padding: '16px 13px 13px',
                opacity: arrastrando && esValida === false ? 0.32 : 1,
                filter: arrastrando && esValida === false ? 'grayscale(.5)' : 'none',
                transform: hover && esValida ? 'translateY(-2px)' : 'none',
                transition: 'all .15s ease',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: COLOR_POR_ESTADO[columna] }} />
                  <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), fontWeight: 500, color: TOKENS.ink }}>
                    {ESTADOS_LABEL[columna]}
                  </Typography>
                </Box>
                <Box sx={{ backgroundColor: '#fff', border: `1px solid ${TOKENS.hair}`, borderRadius: '999px', padding: '1px 8px' }}>
                  <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.text }}>{items.length}</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11.5), color: TOKENS.muted, marginBottom: '10px' }}>
                {formatMoneda(total)}
              </Typography>

              <Box data-testid={`kanban-column-scroll-${columna}`} sx={{ maxHeight: '478px', overflowY: 'auto', paddingRight: '4px' }}>
              {items.length === 0 ? (
                <Box sx={{ border: `1px dashed ${TOKENS.faint}`, borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11.5), color: TOKENS.faint }}>Vacío</Typography>
                </Box>
              ) : (
                items.map((item) => {
                  const dias = item.validez && item.estado_codigo === EstadoBolsa.ENVIADA ? diasHasta(item.validez) : null;
                  return (
                    <Box
                      key={item.id}
                      data-testid="kanban-card"
                      draggable
                      onDragStart={() => setArrastrando({ id: item.id, estado: item.estado_codigo })}
                      onDragEnd={() => setArrastrando(null)}
                      onClick={() => onAbrirDrawer(item.id)}
                      sx={{
                        position: 'relative',
                        backgroundColor: '#fff',
                        borderRadius: '14px',
                        padding: '13px 15px 12px 17px',
                        marginBottom: '10px',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,.65), 0 2px 4px rgba(46,36,48,.07), 0 8px 20px rgba(46,36,48,.11)',
                        cursor: 'grab',
                        opacity: arrastrando?.id === item.id ? 0.45 : 1,
                        transform: arrastrando?.id === item.id ? 'rotate(2deg) scale(.97)' : 'none',
                        transition: 'transform .15s ease',
                        '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 4px 8px rgba(46,36,48,.09), 0 16px 34px rgba(46,36,48,.17)' },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 12,
                          bottom: 12,
                          width: 3.5,
                          borderRadius: '999px',
                          backgroundColor: COLOR_POR_ESTADO[columna],
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '6px' }}>
                        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), fontWeight: 600, color: TOKENS.ink }}>
                          {item.codigo_cotizacion ?? item.id}
                        </Typography>
                        {/* SPEC-114 (RN-001, RN-002, RN-003) — navega directo al detalle, sin abrir el drawer ni iniciar arrastre */}
                        <Tooltip title="Ver detalle" arrow>
                          <Box
                            component="button"
                            type="button"
                            data-testid="kanban-ver-detalle"
                            aria-label="Ver detalle"
                            draggable={false}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              onVerDetalle(item.id);
                            }}
                            sx={{
                              flexShrink: 0,
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              border: 'none',
                              backgroundColor: 'transparent',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              color: TOKENS.muted,
                              '&:hover': { backgroundColor: 'rgba(147,64,143,.10)', color: TOKENS.purple },
                            }}
                          >
                            <Eye size={13} strokeWidth={2} />
                          </Box>
                        </Tooltip>
                      </Box>
                      {item.estado_codigo === EstadoBolsa.GANADA && item.numero_pedido ? (
                        <Typography
                          data-testid="kanban-card-numero-pedido"
                          sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11.5), fontWeight: 600, color: TOKENS.purple }}
                        >
                          {item.numero_pedido}
                        </Typography>
                      ) : null}
                      <Typography
                        sx={{
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: fs(11.5),
                          color: TOKENS.muted,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.cliente_razon_social ?? '—'}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${TOKENS.hair}`, marginTop: '8px', paddingTop: '6px' }}>
                        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), fontWeight: 700, color: TOKENS.purple }}>
                          {formatMoneda(item.total)}
                        </Typography>
                        {dias !== null ? (
                          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), fontWeight: 700, color: TOKENS.amber }}>
                            vence en {dias} d
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })
              )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
